// Shared utilities for hosted-sites components: formatting, URL resolver, URL filter parsing, live version lookup

import { SITES_BASE } from '../../lib/api-client'
import type { HostedSite, SiteFilters, SiteVersion } from '../../hooks/use-hosted-sites'

export const DESCRIPTION_MAX = 500

export type DateRange = 'all' | '7d' | '30d' | '90d'
export type SiteStatus = NonNullable<SiteFilters['status']>
export type SiteType = NonNullable<SiteFilters['siteType']>

export const SITE_STATUSES: readonly SiteStatus[] = ['ready', 'processing', 'failed']
export const SITE_TYPES: readonly SiteType[] = ['html', 'zip']
export const DATE_RANGES: readonly DateRange[] = ['all', '7d', '30d', '90d']

const DAY_MS = 86_400e3
const PRESET_MS: Record<Exclude<DateRange, 'all'>, number> = { '7d': 7 * DAY_MS, '30d': 30 * DAY_MS, '90d': 90 * DAY_MS }

export interface SiteUrlFilters {
  status: SiteStatus | ''
  siteType: SiteType | ''
  dateRange: DateRange
}

function pick<T extends string>(allowed: readonly T[], raw: string | null): T | undefined {
  return (allowed as readonly string[]).includes(raw ?? '') ? (raw as T) : undefined
}

/** Read filters from the URL; unknown values fall back to "no filter" so a stray ?status=foo never reaches the API as a 400 */
export function parseSiteFilters(params: URLSearchParams): SiteUrlFilters {
  return {
    status: pick(SITE_STATUSES, params.get('status')) ?? '',
    siteType: pick(SITE_TYPES, params.get('siteType')) ?? '',
    dateRange: pick(DATE_RANGES, params.get('dateRange')) ?? 'all',
  }
}

/** Write filters into a copy of the URL params, dropping defaults and keeping unrelated params */
export function applySiteFilters(prev: URLSearchParams, filters: SiteUrlFilters): URLSearchParams {
  const next = new URLSearchParams(prev)
  if (filters.status) next.set('status', filters.status)
  else next.delete('status')
  if (filters.siteType) next.set('siteType', filters.siteType)
  else next.delete('siteType')
  if (filters.dateRange !== 'all') next.set('dateRange', filters.dateRange)
  else next.delete('dateRange')
  return next
}

/** Unix ms lower bound for a date-range preset, undefined for "all time" */
export function createdAfterFor(range: DateRange, now: number): number | undefined {
  return range === 'all' ? undefined : now - PRESET_MS[range]
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function formatDate(ts: number, locale?: string): string {
  return new Date(ts).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' })
}

/**
 * Id of the version currently served. The site only reports its live version label, so pick the newest entry
 * with that label (the list is newest first); without a label the newest entry is live.
 */
export function liveVersionId(versions: readonly SiteVersion[], liveLabel: string | undefined): string | null {
  if (versions.length === 0) return null
  if (!liveLabel) return versions[0].id
  return versions.find((v) => v.version === liveLabel)?.id ?? null
}

/** Full public URL for a hosted site (served from separate sites domain for credential isolation, GH-121) */
export function sitePublicUrl(site: HostedSite): string {
  if (SITES_BASE) return `${SITES_BASE}${site.url}`
  // Derive sites origin from current origin: app.X → sites.X, localhost → api.X (dev fallback)
  const origin = window.location.origin
  if (origin.includes('app.')) return `${origin.replace('app.', 'sites.')}${site.url}`
  // Dev: sites served from same origin via Vite proxy
  return `${origin}${site.url}`
}
